(() => {
  const formatMoney = (cents, format) => {
    const value = Number(cents || 0);
    const amount = (value / 100).toFixed(2);
    const noDecimals = String(Math.round(value / 100));

    return String(format || "${{amount}}")
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals);
  };

  const initGallery = (root, signal, cleanups) => {
    const gallery = root.querySelector("[data-product-gallery]");
    if (!gallery) return;

    const stage = gallery.querySelector("[data-gallery-stage]");
    const slides = Array.from(gallery.querySelectorAll("[data-gallery-slide]"));
    const thumbs = Array.from(gallery.querySelectorAll("[data-gallery-thumb]"));
    const thumbRail = gallery.querySelector(".mpdp-gallery__thumbs");
    const progress = gallery.querySelector(".mpdp-gallery__progress");
    const progressFill = gallery.querySelector("[data-gallery-progress]");
    const status = gallery.querySelector("[data-gallery-status]");
    const duration = Math.max(1000, Number(root.dataset.galleryDuration || 5000));
    // This PDP intentionally rotates whenever multiple media items exist.
    // The progress indicator and automatic media storytelling are part of
    // the product-gallery UX, so do not allow an old section setting to
    // silently disable the experience.
    const autoplay = slides.length > 1;

    let index = 0;
    let timer = null;
    let paused = false;

    let swipeStartX = 0;
    let swipeStartY = 0;
    let swipeDeltaX = 0;
    let swipeActive = false;
    let swipeSlide = null;

    const updateStatus = () => {
      gallery.classList.toggle("is-paused", paused);
      stage?.setAttribute("aria-pressed", String(paused));
      stage?.setAttribute(
        "aria-label",
        paused
          ? "Resume product media slideshow"
          : "Pause product media slideshow"
      );
      if (status) {
        status.textContent = paused ? "Slideshow paused" : "Slideshow playing";
      }
    };

    const restartProgress = () => {
      if (!progress || !autoplay || paused) return;

      progress.classList.remove("is-running");
      if (progressFill) progressFill.style.animation = "none";
      void progress.offsetWidth;
      if (progressFill) progressFill.style.animation = "";
      progress.style.setProperty("--gallery-duration", `${duration}ms`);
      progress.classList.add("is-running");
    };

    const schedule = () => {
      window.clearTimeout(timer);
      if (!autoplay || paused) return;

      restartProgress();
      timer = window.setTimeout(() => show(index + 1, true), duration);
    };

    const show = (nextIndex, automatic = false) => {
      if (!slides.length) return;

      index = (Number(nextIndex) + slides.length) % slides.length;

      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle("is-active", active);
        slide.hidden = !active;

        const video = slide.querySelector("video");
        if (video && !active) video.pause();
      });

      thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === index;
        thumb.classList.toggle("is-active", active);
        thumb.setAttribute("aria-pressed", String(active));
      });

      if (thumbRail && thumbs[index]) {
        const thumb = thumbs[index];
        const isMobileVertical = window.matchMedia("(max-width: 749px)").matches;

        if (isMobileVertical) {
          const targetTop =
            thumb.offsetTop -
            Math.max(0, (thumbRail.clientHeight - thumb.offsetHeight) / 2);

          thumbRail.scrollTo({
            top: Math.max(0, targetTop),
            behavior: "smooth"
          });
        } else {
          const targetLeft =
            thumb.offsetLeft -
            Math.max(0, (thumbRail.clientWidth - thumb.offsetWidth) / 2);

          thumbRail.scrollTo({
            left: Math.max(0, targetLeft),
            behavior: "smooth"
          });
        }
      }

      schedule();
    };

    const toggle = () => {
      if (!autoplay) return;

      paused = !paused;
      window.clearTimeout(timer);

      if (paused) {
        progress?.classList.remove("is-running");
      } else {
        schedule();
      }

      updateStatus();
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener(
        "click",
        () => show(thumb.dataset.galleryThumb),
        { signal }
      );
    });

    stage?.addEventListener("click", (event) => {
      if (event.target.closest("video, iframe, button, a")) return;
      if (window.matchMedia("(max-width: 749px)").matches) return;
      toggle();
    }, { signal });

    stage?.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      toggle();
    }, { signal });

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        window.clearTimeout(timer);
        progress?.classList.remove("is-running");
      } else if (!paused) {
        schedule();
      }
    }, { signal });

    // Mobile: horizontal swipe changes product media while vertical motion
    // remains normal page scrolling.
    stage?.addEventListener("touchstart", (event) => {
      if (!window.matchMedia("(max-width: 749px)").matches) return;
      if (!event.touches?.length) return;

      const touch = event.touches[0];
      swipeStartX = touch.clientX;
      swipeStartY = touch.clientY;
      swipeDeltaX = 0;
      swipeActive = true;
      swipeSlide = slides[index] || null;

      if (swipeSlide) swipeSlide.style.transition = "none";
    }, { signal, passive: true });

    stage?.addEventListener("touchmove", (event) => {
      if (!swipeActive || !event.touches?.length || !swipeSlide) return;

      const touch = event.touches[0];
      const dx = touch.clientX - swipeStartX;
      const dy = touch.clientY - swipeStartY;

      // Do not hijack vertical scrolling.
      if (Math.abs(dy) >= Math.abs(dx)) return;

      swipeDeltaX = dx;
      const width = Math.max(1, stage.clientWidth);
      const ratio = Math.min(1, Math.abs(dx) / width);

      swipeSlide.style.transform = `translateX(${dx * 0.45}px)`;
      swipeSlide.style.opacity = String(Math.max(0.72, 1 - ratio * 0.22));
    }, { signal, passive: true });

    const finishSwipe = () => {
      if (!swipeActive) return;

      const activeSlide = swipeSlide;
      const threshold = Math.min(52, Math.max(34, (stage?.clientWidth || 1) * 0.18));
      const shouldChange = Math.abs(swipeDeltaX) >= threshold;
      const direction = swipeDeltaX < 0 ? 1 : -1;

      swipeActive = false;
      swipeSlide = null;

      if (activeSlide) {
        activeSlide.style.transition = "";
        activeSlide.style.transform = "";
        activeSlide.style.opacity = "";
      }

      if (shouldChange) show(index + direction);
      swipeDeltaX = 0;
    };

    stage?.addEventListener("touchend", finishSwipe, { signal, passive: true });
    stage?.addEventListener("touchcancel", finishSwipe, { signal, passive: true });

    updateStatus();
    show(0, true);
    cleanups.push(() => window.clearTimeout(timer));
  };

  const initBuybox = (root, signal) => {
    const form = root.querySelector("[data-mpdp-form]");
    if (!form) return;

    const moneyFormat = root.dataset.moneyFormat || "${{amount}}";
    const stripsPerPack = Number(root.dataset.stripsPerPack || 30);
    const oneTimePrice = Number(root.dataset.oneTimePrice || root.dataset.basePrice || 0);
    const storeDomain = root.dataset.storeDomain || window.location.hostname;
    const variantGid = root.dataset.variantGid || "";
    const storefrontEndpoint = `https://${storeDomain}/api/2026-07/graphql.json`;
    const previewCache = new Map();
    let previewRequestId = 0;

    const subscriptionPlan = {
      id: root.dataset.subscriptionPlanId || "",
      gid: root.dataset.subscriptionPlanGid || "",
      name: root.dataset.subscriptionPlanName || "Subscribe & Save",
      unit: Number(root.dataset.subscriptionPlanPrice || oneTimePrice),
      compareUnit: Number(root.dataset.subscriptionPlanComparePrice || oneTimePrice),
      renewalUnit: Number(root.dataset.subscriptionPlanRenewalPrice || root.dataset.subscriptionPlanPrice || oneTimePrice)
    };
    const hasSubscription = Boolean(subscriptionPlan.id && subscriptionPlan.gid);

    const modeButtons = Array.from(root.querySelectorAll("[data-mode]"));
    const modeSubtitle = root.querySelector('[data-mode="subscription"] [data-mode-subtitle]');
    const subscriptionBenefits = root.querySelector("[data-subscription-benefits]");
    const subscriptionExtraSaving = root.querySelector("[data-subscription-extra-saving]");
    const packRows = Array.from(root.querySelectorAll("[data-pack]"));
    const quantityInput = root.querySelector("[data-purchase-quantity]");
    const sellingPlanInput = root.querySelector("[data-selling-plan-input]");
    const customSelect = root.querySelector("[data-custom-pack-select]");
    const customQuantity = root.querySelector("[data-custom-quantity]");
    const quantityHelper = root.querySelector("[data-quantity-helper]");

    const orderPacks = root.querySelector("[data-order-packs]");
    const orderCadence = root.querySelector("[data-order-cadence]");
    const orderTotal = root.querySelector("[data-order-total]");
    const savingsPanel = root.querySelector("[data-order-savings-panel]");
    const quantitySavingRow = root.querySelector("[data-order-quantity-saving-row]");
    const quantitySavingLabel = root.querySelector("[data-order-quantity-saving-label]");
    const quantitySavingAmount = root.querySelector("[data-order-quantity-saving]");
    const subscriptionSavingRow = root.querySelector("[data-order-subscription-saving-row]");
    const subscriptionSavingLabel = root.querySelector("[data-order-subscription-saving-label]");
    const subscriptionSavingAmount = root.querySelector("[data-order-subscription-saving]");
    const savingsStackNote = root.querySelector("[data-order-savings-stack-note]");
    const totalSavingRow = root.querySelector("[data-order-total-saving-row]");
    const totalSavingLabel = root.querySelector("[data-order-total-saving-label]");
    const totalSavingAmount = root.querySelector("[data-order-total-saving]");
    const submitLabel = root.querySelector("[data-submit-label]");
    const submitSubtitle = root.querySelector("[data-submit-subtitle]");

    let mode = hasSubscription ? "subscription" : "one-time";
    let selectedPack =
      packRows.find((row) => Number(row.dataset.packQuantity) === 1) ||
      packRows[0] || null;
    let quantity = Number(selectedPack?.dataset.packQuantity || 1);

    const clampPercent = (value) => Math.max(0, Math.min(100, Number(value || 0)));
    const formatPercent = (value) => {
      const rounded = Math.round(clampPercent(value) * 10) / 10;
      return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
    };
    const subscriptionPlanSavingPercent = oneTimePrice > 0
      ? clampPercent(((oneTimePrice - subscriptionPlan.unit) / oneTimePrice) * 100)
      : 0;

    const previewOneTimeQuantity = async (qty) => {
      const quantityToPrice = Math.max(1, Number(qty || 1));
      const key = `one-time:${variantGid}:${quantityToPrice}`;
      if (previewCache.has(key)) return previewCache.get(key);
      if (!variantGid) return null;

      const query = `
        mutation MeltivaPricePreview($merchandiseId: ID!, $quantity: Int!) {
          cartCreate(input: {
            lines: [{ merchandiseId: $merchandiseId, quantity: $quantity }]
          }) {
            cart {
              lines(first: 5) {
                nodes {
                  quantity
                  cost {
                    subtotalAmount { amount currencyCode }
                    totalAmount { amount currencyCode }
                  }
                  discountAllocations {
                    discountedAmount { amount currencyCode }
                  }
                }
              }
            }
            userErrors { field message }
          }
        }
      `;

      try {
        const response = await fetch(storefrontEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            query,
            variables: { merchandiseId: variantGid, quantity: quantityToPrice }
          })
        });
        if (!response.ok) throw new Error(`Storefront preview ${response.status}`);
        const payload = await response.json();
        const mutation = payload?.data?.cartCreate;
        if (payload?.errors?.length || mutation?.userErrors?.length) {
          throw new Error(payload?.errors?.[0]?.message || mutation?.userErrors?.[0]?.message || "Unable to price cart");
        }
        const line = mutation?.cart?.lines?.nodes?.[0];
        if (!line) return null;
        const subtotal = Math.round(Number(line.cost?.subtotalAmount?.amount || 0) * 100);
        const total = Math.round(Number(line.cost?.totalAmount?.amount || 0) * 100);
        const compare = subtotal > 0 ? subtotal : oneTimePrice * quantityToPrice;
        const result = {
          quantity: quantityToPrice,
          compare,
          total: total > 0 ? total : oneTimePrice * quantityToPrice
        };
        result.saved = Math.max(0, result.compare - result.total);
        result.unit = Math.round(result.total / quantityToPrice);
        result.savingPercent = result.compare > 0 ? clampPercent((result.saved / result.compare) * 100) : 0;
        previewCache.set(key, result);
        return result;
      } catch (error) {
        console.warn("[Meltiva PDP] Shopify one-time price preview unavailable", error);
        return null;
      }
    };

    const previewSubscriptionQuantity = async (qty) => {
      const quantityToPrice = Math.max(1, Number(qty || 1));
      if (!variantGid || !subscriptionPlan.gid) return null;
      const key = `subscription:${variantGid}:${subscriptionPlan.gid}:${quantityToPrice}`;
      if (previewCache.has(key)) return previewCache.get(key);

      const query = `
        mutation MeltivaSubscriptionPricePreview($merchandiseId: ID!, $quantity: Int!, $sellingPlanId: ID!) {
          cartCreate(input: {
            lines: [{ merchandiseId: $merchandiseId, quantity: $quantity, sellingPlanId: $sellingPlanId }]
          }) {
            cart {
              lines(first: 5) {
                nodes {
                  quantity
                  cost {
                    subtotalAmount { amount currencyCode }
                    totalAmount { amount currencyCode }
                  }
                  discountAllocations {
                    discountedAmount { amount currencyCode }
                  }
                  sellingPlanAllocation { sellingPlan { id name } }
                }
              }
            }
            userErrors { field message }
          }
        }
      `;

      try {
        const response = await fetch(storefrontEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            query,
            variables: {
              merchandiseId: variantGid,
              quantity: quantityToPrice,
              sellingPlanId: subscriptionPlan.gid
            }
          })
        });
        if (!response.ok) throw new Error(`Storefront subscription preview ${response.status}`);
        const payload = await response.json();
        const mutation = payload?.data?.cartCreate;
        if (payload?.errors?.length || mutation?.userErrors?.length) {
          throw new Error(payload?.errors?.[0]?.message || mutation?.userErrors?.[0]?.message || "Unable to price subscription cart");
        }
        const line = mutation?.cart?.lines?.nodes?.[0];
        if (!line) return null;
        const total = Math.round(Number(line.cost?.totalAmount?.amount || 0) * 100);
        const compare = oneTimePrice * quantityToPrice;
        const result = {
          quantity: quantityToPrice,
          compare,
          total: total > 0 ? total : subscriptionPlan.unit * quantityToPrice
        };
        result.saved = Math.max(0, result.compare - result.total);
        result.unit = Math.round(result.total / quantityToPrice);
        result.savingPercent = result.compare > 0 ? clampPercent((result.saved / result.compare) * 100) : 0;
        result.renewal = result.total;
        previewCache.set(key, result);
        return result;
      } catch (error) {
        console.warn("[Meltiva PDP] Shopify subscription price preview unavailable", error);
        return null;
      }
    };

    const setMode = (nextMode) => {
      mode = nextMode === "subscription" && hasSubscription ? "subscription" : "one-time";
      render();
    };

    const setPack = (pack) => {
      if (!pack) return;
      selectedPack = pack;
      if (pack.classList.contains("mpdp-pack--custom") && customSelect) {
        quantity = Number(customSelect.value || 3);
        pack.dataset.packQuantity = String(quantity);
      } else {
        quantity = Number(pack.dataset.packQuantity || 1);
      }
      render();
    };

    const updateSelection = () => {
      modeButtons.forEach((button) => {
        const active = button.dataset.mode === mode;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
      });

      packRows.forEach((row) => {
        const active = row === selectedPack;
        row.classList.toggle("is-selected", active);
        const control = row.matches("button") ? row : row.querySelector("[data-custom-pack-button]");
        control?.setAttribute("aria-pressed", String(active));
      });

      if (subscriptionBenefits) subscriptionBenefits.hidden = mode !== "subscription";
      if (customQuantity) customQuantity.hidden = !selectedPack?.classList.contains("mpdp-pack--custom");
      form.classList.toggle("is-subscription", mode === "subscription");
      form.classList.toggle("is-one-time", mode === "one-time");
    };

    const updatePackPricing = async () => {
      const requestId = ++previewRequestId;

      if (mode === "subscription") {
        await Promise.all(packRows.map(async (row) => {
          const q = Number(row.dataset.packQuantity || 1);
          const preview = await previewSubscriptionQuantity(q);
          if (requestId !== previewRequestId || mode !== "subscription") return;
          const unitEl = row.querySelector("[data-pack-unit-price]");
          const saveEl = row.querySelector("[data-pack-save]");
          const unit = preview?.unit ?? subscriptionPlan.unit;
          const savingPercent = preview?.savingPercent ?? subscriptionPlanSavingPercent;
          if (unitEl) unitEl.textContent = formatMoney(unit, moneyFormat);
          if (saveEl) {
            saveEl.hidden = savingPercent <= 0;
            saveEl.textContent = savingPercent > 0 ? `Save ${formatPercent(savingPercent)} total` : "";
          }
        }));

        if (modeSubtitle) {
          modeSubtitle.textContent = subscriptionPlanSavingPercent > 0
            ? `Save ${formatPercent(subscriptionPlanSavingPercent)} extra on recurring delivery`
            : "Recurring delivery";
        }
        if (subscriptionExtraSaving) {
          subscriptionExtraSaving.textContent = subscriptionPlanSavingPercent > 0
            ? `Extra ${formatPercent(subscriptionPlanSavingPercent)} off`
            : "Subscriber pricing";
        }
        if (quantityHelper) quantityHelper.textContent = "Choose how many packs ship every month.";
        return;
      }

      if (quantityHelper) quantityHelper.textContent = "Quantity savings are applied automatically.";
      await Promise.all(packRows.map(async (row) => {
        const q = Number(row.dataset.packQuantity || 1);
        const preview = await previewOneTimeQuantity(q);
        if (requestId !== previewRequestId || mode !== "one-time") return;
        const unitEl = row.querySelector("[data-pack-unit-price]");
        const saveEl = row.querySelector("[data-pack-save]");
        const unit = preview?.unit ?? oneTimePrice;
        const savingPercent = preview?.savingPercent ?? 0;
        if (unitEl) unitEl.textContent = formatMoney(unit, moneyFormat);
        if (saveEl) {
          saveEl.hidden = savingPercent <= 0;
          saveEl.textContent = savingPercent > 0 ? `Save ${formatPercent(savingPercent)}` : "";
        }
      }));
    };

    const updateInputs = () => {
      quantity = Number(selectedPack?.dataset.packQuantity || quantity || 1);
      if (quantityInput) quantityInput.value = String(quantity);
      if (sellingPlanInput) {
        const usePlan = mode === "subscription" && hasSubscription;
        sellingPlanInput.disabled = !usePlan;
        sellingPlanInput.value = usePlan ? subscriptionPlan.id : "";
      }
    };

    const setSavingRow = (row, amountEl, amount) => {
      if (!row || !amountEl) return;
      const visible = amount > 0;
      row.hidden = !visible;
      amountEl.textContent = visible ? `−${formatMoney(amount, moneyFormat)}` : "";
    };

    const updateOrder = async () => {
      const renderMode = mode;
      const q = Number(selectedPack?.dataset.packQuantity || quantity || 1);
      const baseTotal = oneTimePrice * q;
      const oneTimePreview = await previewOneTimeQuantity(q);
      if (mode !== renderMode) return;
      const oneTimeTotal = oneTimePreview?.total ?? baseTotal;

      let finalTotal = oneTimeTotal;
      let renewalTotal = oneTimeTotal;
      let subscriptionSavings = 0;

      if (renderMode === "subscription") {
        const subscriptionPreview = await previewSubscriptionQuantity(q);
        if (mode !== renderMode) return;
        finalTotal = subscriptionPreview?.total ?? subscriptionPlan.unit * q;
        renewalTotal = subscriptionPreview?.renewal ?? subscriptionPlan.renewalUnit * q;
        subscriptionSavings = Math.max(0, oneTimeTotal - finalTotal);
      }

      const quantitySavings = Math.max(0, baseTotal - oneTimeTotal);
      const totalSavings = Math.max(0, baseTotal - finalTotal);

      if (orderPacks) {
        orderPacks.textContent = `${q} ${q === 1 ? "pack" : "packs"} · ${q * stripsPerPack} strips`;
      }
      if (orderCadence) {
        orderCadence.textContent = renderMode === "subscription" ? "Ships every month" : "one-time purchase";
      }
      if (orderTotal) orderTotal.textContent = formatMoney(finalTotal, moneyFormat);

      const quantitySavingPercent = baseTotal > 0
        ? (quantitySavings / baseTotal) * 100
        : 0;
      const subscriptionSavingPercent = oneTimeTotal > 0
        ? (subscriptionSavings / oneTimeTotal) * 100
        : 0;
      const totalSavingPercent = baseTotal > 0
        ? (totalSavings / baseTotal) * 100
        : 0;

      if (quantitySavingLabel) {
        quantitySavingLabel.textContent = `${q}-pack savings · ${formatPercent(quantitySavingPercent)}`;
      }
      if (subscriptionSavingLabel) {
        subscriptionSavingLabel.textContent = `Subscribe & Save · extra ${formatPercent(subscriptionSavingPercent)}`;
      }
      if (totalSavingLabel) {
        totalSavingLabel.textContent = `Total savings · ${formatPercent(totalSavingPercent)}`;
      }

      setSavingRow(quantitySavingRow, quantitySavingAmount, quantitySavings);
      setSavingRow(subscriptionSavingRow, subscriptionSavingAmount, subscriptionSavings);

      if (savingsStackNote) {
        const explainStacking = renderMode === "subscription" && quantitySavings > 0 && subscriptionSavings > 0;
        savingsStackNote.hidden = !explainStacking;
      }

      if (totalSavingRow && totalSavingAmount) {
        totalSavingRow.hidden = totalSavings <= 0;
        totalSavingAmount.textContent = totalSavings > 0 ? formatMoney(totalSavings, moneyFormat) : "";
      }
      if (savingsPanel) savingsPanel.hidden = totalSavings <= 0;

      if (submitLabel) {
        submitLabel.textContent = renderMode === "subscription"
          ? totalSavings > 0
            ? `Subscribe & Save ${formatMoney(totalSavings, moneyFormat)}`
            : "Subscribe & Save"
          : "Add to cart";
      }
      if (submitSubtitle) {
        submitSubtitle.textContent = renderMode === "subscription"
          ? `Renews at ${formatMoney(renewalTotal, moneyFormat)} every month · Skip, pause, or cancel anytime.`
          : "One-time purchase · No recurring charge.";
      }
    };

    const render = () => {
      updateSelection();
      void updatePackPricing();
      updateInputs();
      void updateOrder();
    };

    modeButtons.forEach((button) => {
      button.addEventListener("click", () => setMode(button.dataset.mode || "one-time"), { signal });
    });

    packRows.forEach((row) => {
      if (row.matches("button")) {
        row.addEventListener("click", () => setPack(row), { signal });
      } else {
        row.querySelector("[data-custom-pack-button]")?.addEventListener("click", () => setPack(row), { signal });
      }
    });

    customSelect?.addEventListener("change", () => {
      const customRow = root.querySelector(".mpdp-pack--custom");
      if (!customRow) return;
      quantity = Number(customSelect.value || 3);
      customRow.dataset.packQuantity = String(quantity);
      setPack(customRow);
    }, { signal });

    [1, 2, 3].forEach((qty) => {
      void previewOneTimeQuantity(qty);
      if (hasSubscription) void previewSubscriptionQuantity(qty);
    });
    render();
  };

  const initRoot = (root) => {
    if (!root || root.dataset.mpdpReady === "true") return;

    root.dataset.mpdpReady = "true";
    const controller = new AbortController();
    const cleanups = [];

    initGallery(root, controller.signal, cleanups);
    initBuybox(root, controller.signal);

    root.__mpdpCleanup = () => {
      controller.abort();
      cleanups.forEach((cleanup) => cleanup());
      delete root.dataset.mpdpReady;
    };
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll("[data-meltiva-pdp]").forEach(initRoot);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll("[data-meltiva-pdp]").forEach((root) => {
      root.__mpdpCleanup?.();
    });
  });
})();
